<script lang="ts">
  import { login, authState } from '../../lib/auth.svelte';
  import LoadingSpinner from '../ui/LoadingSpinner.svelte';

  let email = $state('');
  let password = $state('');
  let showPassword = $state(false);
  let rememberMe = $state(false);
  let error = $state('');
  let loading = $state(false);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    error = '';
    loading = true;

    const result = await login(email, password);

    if (!result.success) {
      error = result.error || 'Login failed';
    }

    loading = false;
  }

  function navigateTo(path: string) {
    window.location.hash = path;
  }

  function togglePasswordVisibility() {
    showPassword = !showPassword;
  }
</script>

<div class="login-container">
  <!-- Left Panel - Form -->
  <div class="form-panel">
    <div class="form-content">
      <!-- Logo and Welcome -->
      <div class="welcome-section">
        <p class="welcome-text">WELCOME TO</p>
        <img src="/console/assets/app_logo.png" alt="EQUOS" class="logo-icon" />
        <span class="logo-text">EQUOS9</span>
        <p class="subtitle">Log in to access your business management dashboard</p>
      </div>

      <!-- Error Message -->
      {#if error}
        <div class="error-message" role="alert">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>{error}</span>
        </div>
      {/if}

      <!-- Form -->
      <form onsubmit={handleSubmit}>
        <div class="form-group">
          <div class="input-wrapper">
            <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            <input
              id="email"
              type="email"
              bind:value={email}
              required
              placeholder="Email"
              disabled={loading}
              autocomplete="email"
            />
          </div>
        </div>

        <div class="form-group">
          <div class="input-wrapper">
            <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              bind:value={password}
              required
              placeholder="Password"
              disabled={loading}
              autocomplete="current-password"
            />
            <button
              type="button"
              class="password-toggle"
              onclick={togglePasswordVisibility}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {#if showPassword}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              {:else}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              {/if}
            </button>
          </div>
        </div>

        <!-- Remember Me & Forgot Password Row -->
        <div class="options-row">
          <label class="remember-me">
            <input type="checkbox" bind:checked={rememberMe} />
            <span class="checkmark"></span>
            <span>Remember me</span>
          </label>
          <button
            type="button"
            class="forgot-link"
            onclick={() => navigateTo('reset-password')}
          >
            Forgot Password?
          </button>
        </div>

        <!-- Submit Button -->
        <button type="submit" class="btn-primary" disabled={loading}>
          {#if loading}
            <LoadingSpinner size="small" light />
            <span>Signing in...</span>
          {:else}
            <span>SIGN IN</span>
          {/if}
        </button>
      </form>

      <!-- Sign Up Link -->
      <p class="signup-link">
        Don't have an account?
        <button type="button" onclick={() => navigateTo('signup')}>Sign Up Now</button>
      </p>

      <!-- Divider -->
      <div class="divider">
        <span>Or</span>
      </div>

      <!-- Social Login -->
      <p class="social-text">Continue with social media</p>
      <div class="social-buttons">
        <button type="button" class="social-btn google" disabled>
          <svg viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        </button>
      </div>

      <!-- Version -->
      <p class="version">v1.0.0</p>
    </div>
  </div>

  <!-- Right Panel - Branding -->
  <div class="brand-panel">
    <div class="brand-content">
      <!-- Decorative circles -->
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>

      <!-- Logo -->
      <div class="brand-logo">
        <img src="/console/assets/app_logo.png" alt="EQUOS" class="brand-logo-img" />
      </div>

      <h2 class="brand-name">EQUOS9</h2>
      <p class="brand-tagline">
        Account Management Portal
      </p>
      <p class="brand-description">
        Manage your subscription, team members, and business settings.
        To access the full EQUOS application, visit:
      </p>
      <a href="https://app.equosnine.com" target="_blank" rel="noopener noreferrer" class="app-link">
        Go to App
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
          <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
        </svg>
      </a>
    </div>
  </div>
</div>

<style>
  .login-container {
    min-height: 100vh;
    display: flex;
    flex-direction: row;
  }

  /* ===== LEFT PANEL - FORM ===== */
  .form-panel {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    background: #f8f9fc;
  }

  .form-content {
    width: 100%;
    max-width: 380px;
  }

  /* Welcome Section */
  .welcome-section {
    text-align: center;
    margin-bottom: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .welcome-text {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.15em;
    color: #6b7280;
    margin-bottom: 0.75rem;
  }

  .logo-icon {
    width: 64px;
    height: 64px;
    border-radius: 14px;
    margin-bottom: 0.75rem;
  }

  .logo-text {
    font-size: 1.75rem;
    font-weight: 700;
    color: #1f2937;
    letter-spacing: -0.02em;
    margin-bottom: 0.5rem;
  }

  .subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  /* Error Message */
  .error-message {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 0.875rem 1rem;
    border-radius: 10px;
    margin-bottom: 1.5rem;
    font-size: 0.875rem;
    animation: shake 0.5s ease-in-out;
  }

  .error-message svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    20%, 60% { transform: translateX(-5px); }
    40%, 80% { transform: translateX(5px); }
  }

  /* Form */
  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
  }

  .input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  .input-icon {
    position: absolute;
    left: 1rem;
    width: 20px;
    height: 20px;
    color: #9ca3af;
    pointer-events: none;
    transition: color 0.2s ease;
  }

  .input-wrapper:focus-within .input-icon {
    color: #4A7DFF;
  }

  input[type="email"],
  input[type="password"],
  input[type="text"] {
    width: 100%;
    padding: 0.875rem 1rem 0.875rem 3rem;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    font-size: 1rem;
    background: white;
    color: #1f2937;
    transition: all 0.2s ease;
  }

  input::placeholder {
    color: #9ca3af;
  }

  input:focus {
    outline: none;
    border-color: #4A7DFF;
    box-shadow: 0 0 0 3px rgba(74, 125, 255, 0.1);
  }

  input:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
  }

  .password-toggle {
    position: absolute;
    right: 1rem;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    transition: color 0.2s ease;
  }

  .password-toggle:hover {
    color: #4A7DFF;
  }

  .password-toggle svg {
    width: 20px;
    height: 20px;
  }

  /* Options Row */
  .options-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.25rem;
  }

  .remember-me {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-size: 0.875rem;
    color: #4b5563;
  }

  .remember-me input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: #4A7DFF;
    cursor: pointer;
  }

  .forgot-link {
    background: none;
    border: none;
    color: #4A7DFF;
    font-size: 0.875rem;
    cursor: pointer;
    font-weight: 500;
    transition: color 0.2s ease;
  }

  .forgot-link:hover {
    color: #3b6de0;
    text-decoration: underline;
  }

  /* Submit Button */
  .btn-primary {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.875rem 1.5rem;
    margin-top: 0.5rem;
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 0.9375rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(74, 125, 255, 0.35);
  }

  .btn-primary:active:not(:disabled) {
    transform: translateY(0);
  }

  .btn-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  /* Sign Up Link */
  .signup-link {
    text-align: center;
    margin-top: 1.5rem;
    font-size: 0.875rem;
    color: #6b7280;
  }

  .signup-link button {
    background: none;
    border: none;
    color: #4A7DFF;
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
    transition: color 0.2s ease;
  }

  .signup-link button:hover {
    color: #3b6de0;
  }

  /* Divider */
  .divider {
    display: flex;
    align-items: center;
    margin: 1.5rem 0;
    gap: 1rem;
  }

  .divider::before,
  .divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e5e7eb;
  }

  .divider span {
    font-size: 0.75rem;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }

  /* Social Login */
  .social-text {
    text-align: center;
    font-size: 0.8125rem;
    color: #9ca3af;
    margin-bottom: 1rem;
  }

  .social-buttons {
    display: flex;
    justify-content: center;
    gap: 1rem;
  }

  .social-btn {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 1.5px solid #e5e7eb;
    background: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
  }

  .social-btn:hover:not(:disabled) {
    border-color: #4A7DFF;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .social-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .social-btn svg {
    width: 24px;
    height: 24px;
  }

  /* Version */
  .version {
    text-align: center;
    margin-top: 2rem;
    font-size: 0.75rem;
    color: #9ca3af;
  }

  /* ===== RIGHT PANEL - BRANDING ===== */
  .brand-panel {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    position: relative;
    overflow: hidden;
  }

  .brand-content {
    text-align: center;
    color: white;
    z-index: 1;
    padding: 2rem;
  }

  /* Decorative Circles */
  .circle {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
  }

  .circle-1 {
    width: 300px;
    height: 300px;
    top: -100px;
    right: -50px;
  }

  .circle-2 {
    width: 200px;
    height: 200px;
    bottom: -50px;
    left: -50px;
  }

  .circle-3 {
    width: 150px;
    height: 150px;
    top: 50%;
    right: 10%;
    background: rgba(255, 255, 255, 0.05);
  }

  .brand-logo {
    width: 100px;
    height: 100px;
    margin: 0 auto 1.5rem;
  }

  .brand-logo-img {
    width: 100%;
    height: 100%;
    border-radius: 20px;
    filter: drop-shadow(0 4px 20px rgba(0, 0, 0, 0.15));
  }

  .brand-name {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    letter-spacing: -0.02em;
  }

  .brand-tagline {
    font-size: 1.1rem;
    font-weight: 600;
    opacity: 0.95;
    margin-bottom: 1rem;
  }

  .brand-description {
    font-size: 0.95rem;
    line-height: 1.6;
    opacity: 0.85;
    max-width: 320px;
    margin: 0 auto 1.25rem;
  }

  .app-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    background: white;
    color: #4A7DFF;
    border-radius: 10px;
    font-weight: 600;
    font-size: 1rem;
    text-decoration: none;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .app-link:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }

  .app-link svg {
    width: 18px;
    height: 18px;
  }

  /* ===== RESPONSIVE ===== */
  @media (max-width: 968px) {
    .login-container {
      flex-direction: column-reverse;
    }

    .brand-panel {
      padding: 3rem 2rem;
      min-height: auto;
    }

    .brand-logo {
      width: 70px;
      height: 70px;
      margin-bottom: 1rem;
    }

    .brand-name {
      font-size: 1.75rem;
    }

    .brand-tagline {
      font-size: 0.875rem;
    }

    .circle-1 {
      width: 150px;
      height: 150px;
      top: -50px;
      right: -30px;
    }

    .circle-2 {
      width: 100px;
      height: 100px;
      bottom: -30px;
      left: -30px;
    }

    .circle-3 {
      display: none;
    }

    .form-panel {
      padding: 2rem 1.5rem 3rem;
    }
  }

  @media (max-width: 480px) {
    .form-content {
      max-width: 100%;
    }

    .logo-text {
      font-size: 1.5rem;
    }

    .options-row {
      flex-direction: column;
      gap: 0.75rem;
      align-items: flex-start;
    }
  }
</style>
